import { useQuery } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";
import { Link } from "wouter";
import { gqlRequest } from "../lib/gql";
import {
  PUBLIC_PROFILE,
  PUBLIC_TICKLIST,
  type PublicProfileResult,
  type PublicTicklistResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import { formatDate, formatDuration, type Activity } from "../lib/format";
import { useT } from "../lib/i18n";
import { EntityTabs } from "../components/EntityTabs";
import { OutingCard } from "../components/OutingCard";
import { FollowButton } from "../components/FollowButton";
import { ReportButton } from "../components/ReportButton";
import { Avatar } from "../components/Avatar";
import { PersonRow } from "../components/PersonRow";
import { ProfileMap } from "../map/ProfileMap";
import { ActivityList, EmptyState, ErrorBox, Spinner } from "../components/ui";
import { Badge } from "@/components/ui/badge";

export function PersonPage({ id }: { id: string }) {
  const { personId: viewerId } = useAuth();
  const { t, labelizeT } = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: ["person", id, viewerId],
    // viewerId is null when anonymous; the viewerFollow section then matches
    // nothing, which is exactly the "not following" state we want.
    queryFn: () => gqlRequest<PublicProfileResult>(PUBLIC_PROFILE, { id, viewerId }),
  });

  // Public ticks: RLS shows isPublic rows to everyone; the query's isPublic
  // filter keeps the owner's private rows off their own public profile too.
  const ticks = useQuery({
    queryKey: ["public-ticklist", id],
    queryFn: () => gqlRequest<PublicTicklistResult>(PUBLIC_TICKLIST, { personId: id }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const person = data?.person;
  if (!person) return <EmptyState>{t("person.notFound")}</EmptyState>;

  const publicTicks = ticks.data?.routeTicks.nodes ?? [];
  const followers = data?.followers.nodes ?? [];
  const following = data?.following.nodes ?? [];
  const memberships = data?.outingPeople.nodes ?? [];
  const outings = memberships.map((m) => m.outing);
  const isFollowing = (data?.viewerFollow.nodes.length ?? 0) > 0;
  const activities = (person.activities ?? []) as Activity[];
  const isSelf = viewerId === person.id;

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <Avatar person={person} size="lg" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {person.displayName || t("common.unnamed")}
          </h1>
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <CalendarDays aria-hidden className="size-3.5" />
            {t("person.joined", { date: formatDate(person.createdAt) })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ReportButton entityKind="PERSON" entityId={person.id} />
        <FollowButton personId={person.id} isFollowing={isFollowing} />
      </div>
    </div>
  );

  const profileTab = (
    <div className="space-y-4">
      <div className="flex gap-6">
        <Stat label={t("tabs.followers")} value={data?.followers.totalCount ?? 0} />
        <Stat label={t("tabs.following")} value={data?.following.totalCount ?? 0} />
        <Stat label={t("tabs.outings")} value={data?.outingPeople.totalCount ?? 0} />
      </div>
      <div>
        <h2 className="mb-1.5 text-sm font-semibold tracking-tight text-foreground">{t("person.activities")}</h2>
        {activities.length > 0 ? (
          <p className="text-sm text-muted-foreground">
            <ActivityList activities={activities} />
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {isSelf ? t("person.noneYetSelf") : t("person.noneListed")}
          </p>
        )}
      </div>
      <ProfileMap personId={person.id} />
    </div>
  );

  return (
    <EntityTabs
      header={header}
      tabs={[
        { key: "profile", label: t("tabs.profile"), content: profileTab },
        {
          key: "outings",
          label: t("tabs.outings"),
          badge: data?.outingPeople.totalCount ?? 0,
          content:
            outings.length > 0 ? (
              <div className="space-y-3">
                {outings.map((o) => (
                  <OutingCard key={o.id} outing={o} />
                ))}
              </div>
            ) : (
              <EmptyState>{t("person.noOutings")}</EmptyState>
            ),
        },
        // Only shown when the person has public ticks — an empty section
        // would just advertise a feature the visitor can't act on.
        ...(publicTicks.length > 0
          ? [
              {
                key: "ticklist",
                label: t("tabs.ticklist"),
                badge: publicTicks.length,
                content: (
                  <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
                    {publicTicks.map((tick) => (
                      <li key={tick.id}>
                        <Link
                          href={`/route/${tick.route.id}`}
                          className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-foreground">{tick.route.name}</span>
                            <span className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                              <ActivityList activities={tick.route.activities} />
                              {tick.route.durationMin ? ` · ${formatDuration(tick.route.durationMin)}` : ""}
                              {tick.targetSeason ? ` · ${tick.targetSeason}` : ""}
                            </span>
                          </span>
                          <Badge
                            variant="secondary"
                            className={tick.doneAt ? "text-muted-foreground" : "bg-primary/10 text-primary"}
                          >
                            {tick.doneAt ? t("ticklist.done") : labelizeT(tick.priority)}
                          </Badge>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ),
              },
            ]
          : []),
        {
          key: "followers",
          label: t("tabs.followers"),
          badge: data?.followers.totalCount ?? 0,
          content:
            followers.length > 0 ? (
              <div className="space-y-2">
                {followers.map((f) => (
                  <PersonRow key={f.id} person={f.follower} />
                ))}
              </div>
            ) : (
              <EmptyState>{t("person.noFollowers")}</EmptyState>
            ),
        },
        {
          key: "following",
          label: t("tabs.following"),
          badge: data?.following.totalCount ?? 0,
          content:
            following.length > 0 ? (
              <div className="space-y-2">
                {following.map((f) => (
                  <PersonRow key={f.id} person={f.followee} />
                ))}
              </div>
            ) : (
              <EmptyState>{isSelf ? t("person.notFollowingSelf") : t("person.notFollowing")}</EmptyState>
            ),
        },
      ]}
    />
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
